/**
 * Structured data helper functions for JSON-LD schemas.
 *
 * These generate Organization, WebSite, and FAQ schemas that are
 * injected into the homepage via useStructuredData().
 */

import { pricingTiers } from '@/lib/pricingData';

export function getOrganizationSchema() {
  const offers = pricingTiers.map((tier) => ({
    '@type': 'Offer',
    name: tier.name === 'Unlimited' ? 'Unlimited Plan' : `${tier.name} Plan`,
    price: tier.price.replace('$', ''),
    priceCurrency: 'USD',
  }));

  const lowPrice = Math.min(...offers.map((o) => parseFloat(o.price))).toString();
  const highPrice = Math.max(...offers.map((o) => parseFloat(o.price))).toString();

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'JD Simplified',
    url: 'https://jdsimplified.com',
    logo: 'https://jdsimplified.com/images/logo/jd-simplified-logo.png',
    description:
      'JD Simplified is a practice-first learning system for law students and bar takers.',
    foundingDate: '2023',
    sameAs: ['https://www.youtube.com/@JDsimplified'],
    offers: {
      '@type': 'AggregateOffer',
      offerCount: offers.length.toString(),
      lowPrice,
      highPrice,
      priceCurrency: 'USD',
      offers,
    },
  };
}

export function getWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'JD Simplified',
    url: 'https://jdsimplified.com',
    description:
      'JD Simplified is a practice-first learning system for law students and bar takers.',
    publisher: {
      '@type': 'Organization',
      name: 'JD Simplified, LLC',
    },
  };
}

export function getFAQSchema() {
  const unlimitedTier = pricingTiers.find((t) => t.name === 'Unlimited');

  // Q4 (cost) preserves the dynamic pricingTiers pattern: the monthly price +
  // period are read from pricingData. The $200/year figure is hardcoded because
  // pricingData carries no yearly tier. Must stay in sync with the visible FAQ
  // block in HomePage.tsx.
  const costAnswer = `There's a free plan, and an Unlimited plan at ${unlimitedTier?.price}/${unlimitedTier?.period} or $200/year that includes all study tools, courses, flashcards, and Ask JDS.`;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is JD Simplified?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'JD Simplified is a practice-first learning system for law students and bar takers. It brings your case briefs, outlines, notes, flashcards, and Ask JDS into one connected place — so you can understand the law, practice applying it, and focus on what actually matters.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is it really free?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. You can study, take notes, build outlines, and use core tools on the free plan, with no time limit. Unlimited ($20/month or $200/year) adds everything — all of Study Mode, courses, Issue Spotter, Essay Coach, and Ask JDS.',
        },
      },
      {
        '@type': 'Question',
        name: 'Do I need a credit card to start?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'No. You can sign up and start studying without a card. You only enter payment details if you choose to upgrade to Unlimited.',
        },
      },
      {
        '@type': 'Question',
        name: 'How much does JD Simplified cost?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: costAnswer,
        },
      },
      {
        '@type': 'Question',
        name: 'Can JD Simplified help with the bar exam?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: "Yes. The same skills you build in law school — issue-spotting, applying rules, writing clear analysis — are what the bar tests. When you're ready, NextGen Prep is our practice-first prep built around how the NextGen bar exam actually works.",
        },
      },
      {
        '@type': 'Question',
        name: "What's the difference between JD Simplified and NextGen Prep?",
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'JD Simplified is your day-to-day system for law school. NextGen Prep is practice-first bar prep for the NextGen UBE, built in collaboration with Studicata. One account carries you from your first case brief through bar exam day.',
        },
      },
      {
        '@type': 'Question',
        name: 'Will my work carry over when I start bar prep?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: "Your knowledge and progress carry over — the studying you do in JD Simplified builds on into NextGen Prep, so you start ahead instead of from scratch. (Your bar-readiness is measured fresh inside NextGen Prep's own practice.)",
        },
      },
    ],
  };
}
